import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Search } from 'lucide-react';
import { googleAPI } from '../../utils/api';
import './TechnicianSelectModal.scss';
import '../LoadingSpinner/LoadingSpinner.scss';
import { anonymizeName } from '../../utils/demoMode.js';

const TechnicianSelectModal = ({ isOpen, onClose, onSubmit, title = "Select Technician" }) => {
    const [technicians, setTechnicians] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTechnician, setSelectedTechnician] = useState(null);

    useEffect(() => {
        if (isOpen) {
            loadTechnicians();
        }
    }, [isOpen]);

    const loadTechnicians = async () => {
        try {
            setLoading(true);
            const result = await googleAPI.getTechnicians();
            if (result.success) {
                setTechnicians(result.data || []);
            }
        } catch (error) {
            console.error('Failed to load technicians:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredTechnicians = technicians.filter(tech => 
        tech.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tech.crmCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tech.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        if (selectedTechnician) {
            const valueToSubmit = selectedTechnician.crmCode || selectedTechnician.id;
            onSubmit(valueToSubmit);
            handleClose();
        }
    };

    const handleClose = () => {
        setSearchTerm('');
        setSelectedTechnician(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="modal-overlay" onClick={handleClose}>
                <motion.div
                    className="technician-select-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-header">
                        <h3>
                            <User size={20} />
                            {title}
                        </h3>
                        <button className="close-button" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="modal-content">
                        <div className="search-box">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Search technicians..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </div>

                        {loading ? (
                            <div className="loading-container">
                                <div style={{ background: 'transparent', minHeight: 'auto' }}>
                                    <div className="loading-spinner__container">
                                        <div className="loading-spinner__spinner">
                                            <div className="loading-spinner__inner"></div>
                                        </div>
                                        <p className="loading-spinner__text">Loading...</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="technicians-list">
                                {filteredTechnicians.length === 0 ? (
                                    <div className="no-results">
                                        <User size={32} />
                                        <p>No technicians found</p>
                                    </div>
                                ) : (
                                    filteredTechnicians.map(tech => (
                                        <div
                                            key={tech.id}
                                            className={`technician-item ${selectedTechnician?.id === tech.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedTechnician(tech)}
                                        >
                                            <div className="technician-info">
                                                <h4>{anonymizeName(tech.name)}</h4>
                                                <div className="technician-meta">
                                                    <span className="crm-code">{tech.crmCode}</span>
                                                    {tech.email && <span className="email">{anonymizeName(tech.email.split('@')[0]) + '@example.com'}</span>}
                                                </div>
                                            </div>
                                            <input
                                                type="radio"
                                                name="technician"
                                                checked={selectedTechnician?.id === tech.id}
                                                onChange={() => setSelectedTechnician(tech)}
                                            />
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        <div className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={handleClose}>
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn-primary" 
                                disabled={!selectedTechnician || loading}
                            >
                                Assign
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default TechnicianSelectModal;